import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { CircularProgress } from '@mui/material';
import { Card as SEOCard } from 'src/components/UI/Card';
import AISummary from './components/AISummary';
import AIInsightsPanel from './components/AIInsightsPanel';
import ContentOpportunities from './components/ContentOpportunities';
import ContentClusters from './components/ContentClusters';
import ContentImprovement from './components/ContentImprovement';
import ContentStrategyReport from './components/ContentStrategyReport';
import ContentStrategyBreadcrumbs from './components/ContentStrategyBreadcrumbs';
import ContentStrategyHeader from './components/ContentStrategyHeader';
import ContentStrategyNav from './components/ContentStrategyNav';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import getContentStrategyQuery from './api/getContentStrategy';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'src/redux/store';
import { setOrbitAISnackBar } from 'src/redux/siteAuditReducer';
import { StripeLoader } from 'src/common/StripeLoader';
import CSNoDataFound from './components/CSNoDataFound';
import { useGetAgentsStatus } from '../hooks/useGetAgentsStatus';
import { useGetUserAgentDetails } from '../hooks/useGetUserAgentDetails';
import { useOrbitAISSE } from '../hooks/useOrbitAISSE';
import OrbitAIGuard from '../Components/OrbitAIGuard';
import UpgradeToOrbitAI from '../Components/UpgradeToOrbitAI';
import OrbitAgentSiteGuard from '../Components/OrbitAgentSiteGuard';
import AgentLastRun from '../Components/AgentLastRun';
import { keywordResearchSubmenuTitles } from 'src/common/config/TextMessage';
import { useNavigate } from 'react-router-dom';
import { ContentStrategyPaginationParamsV2 } from '../types/content-strategy-v2.type';
import { useContentStrategyActions } from './hooks/useContentStrategyActions';

const ALL_STRATEGY_DATA_PARAMS: ContentStrategyPaginationParamsV2 = {
  opportunities_limit: 100,
  clusters_limit: 100,
  improvements_limit: 100
};

function ContentStrategyFallback() {
  return (
    <div
      className="-mt-8 pt-8 pb-6"
      style={{
        minHeight: 'calc(100vh - 51px)',
        backgroundColor: 'var(--color-bg-cream)'
      }}
    >
      <div className="flex justify-between items-center mb-3">
        <ContentStrategyBreadcrumbs />
        <ContentStrategyHeader />
      </div>
      <SEOCard>
        <div className="px-6">
          <p className="text-primary font-semibold text-3xl">
            Content Strategy
          </p>
          <div className="w-[600px] mx-auto my-10">
            <UpgradeToOrbitAI />
          </div>
        </div>
      </SEOCard>
    </div>
  );
}

function ContentStrategyContentNew() {
  const navigate = useNavigate();
  const siteAuditDetails = useSelector(
    (state: RootState) => state.siteAudit.siteAuditDetails
  );
  const siteId = siteAuditDetails?._id;
  const user_id = siteAuditDetails?.user_id;
  const siteSoftDeleted = useSelector(
    (state: RootState) => state?.siteAudit?.isSiteSoftDeleted
  );

  const dispatch = useDispatch();

  useOrbitAISSE(siteId);

  const {
    data,
    isLoading,
    isPending,
    isFetching,
    refetch: refetchStrategy
  } = useQuery({
    ...getContentStrategyQuery(user_id, siteId, ALL_STRATEGY_DATA_PARAMS),
    placeholderData: keepPreviousData
  });

  const { data: agentsStatus } = useGetAgentsStatus(siteId);
  const { data: userAgentDetails } = useGetUserAgentDetails(siteId);
  const { addToPlanMutation, ignoreOpportunityMutation } =
    useContentStrategyActions();

  const [highlightOpportunity, setHighlightOpportunity] = useState<{
    id: string;
    ts: number;
  } | null>(null);

  const isContentStrategyProcessing =
    agentsStatus?.agents?.['content-strategy']?.status === 'processing';

  useEffect(() => {
    if (!isContentStrategyProcessing && agentsStatus) {
      refetchStrategy();
    }
  }, [isContentStrategyProcessing, agentsStatus, refetchStrategy]);

  const hasNoData = useMemo(() => {
    if (isContentStrategyProcessing) return false;
    return (
      !data ||
      (!data.content_opportunities?.items?.length &&
        !data.content_clusters?.items?.length &&
        !data.existing_content_improvements?.items?.length &&
        !(data as any)?.content_gaps?.length &&
        !(data as any)?.result?.content_gaps?.length)
    );
  }, [data, isContentStrategyProcessing]);

  const handleSelectOpportunityFromInsight = (opportunityId: string) => {
    setHighlightOpportunity({ id: opportunityId, ts: Date.now() });
  };

  // Todo: Content Plan - Handler to add opportunity to Content Plan
  /*
  const handleAddToPlan = (opportunityId: string) => {
    addToPlanMutation.mutate(
      { opportunityId },
      {
        onSuccess: () => {
          dispatch(
            setOrbitAISnackBar({
              status: 'success',
              message: 'Added to Content Plan'
            })
          );
        }
      }
    );
  };
  */

  const handleIgnoreOpportunity = (
    opportunityId: string,
    customEndpoint?: string
  ) => {
    if (highlightOpportunity?.id === opportunityId) {
      setHighlightOpportunity(null);
    }
    ignoreOpportunityMutation.mutate({ opportunityId, customEndpoint });
  };

  if (isLoading || isPending) {
    return (
      <div
        className="-mt-8 pt-8"
        style={{
          minHeight: 'calc(100vh - 51px)',
          backgroundColor: 'var(--color-bg-cream)'
        }}
      >
        <StripeLoader />
      </div>
    );
  }

  return (
    <div
      className="-mt-8 pt-4 pb-6"
      style={{
        minHeight: 'calc(100vh - 51px)',
        backgroundColor: 'var(--color-bg-cream)'
      }}
    >
      {/** Sticky Header with Breadcrumbs and Section Navigation */}
      <div className="sticky top-[63px] z-10 bg-[var(--color-bg-cream)] -mx-5 px-5 pt-3 pb-3 mb-4 border-b border-[#EAECF0] transition-all">
        <div className="flex justify-between items-center mb-2.5">
          <ContentStrategyBreadcrumbs />
          <div className="flex flex-col md:flex-row items-center gap-4">
            <AgentLastRun
              timestamp={
                !hasNoData && data?.last_updated ? data.last_updated : null
              }
              showBorder
            />
            <ContentStrategyHeader />
          </div>
        </div>
      </div>

      <div className="mt-2">
        {isContentStrategyProcessing ? (
          <SEOCard>
            <div className="px-6">
              <p className="text-primary font-semibold text-3xl">
                Content Strategy
              </p>
              <div className="flex flex-col items-center justify-center gap-6 p-6 pb-6 w-full mt-2.5 mb-2.5">
                <div className="flex flex-col items-center justify-center gap-3 text-center min-h-[16rem]">
                  <CircularProgress size={64} />
                  <p className="font-semibold text-lg text-[#1D2939] mt-2">
                    Orbit AI Content Strategy is running...
                  </p>
                  <p className="font-normal text-sm text-[#667085] max-w-md">
                    This may take a few moments while we generate your
                    customized content strategy and topic recommendations.
                  </p>
                </div>
              </div>
            </div>
          </SEOCard>
        ) : hasNoData ? (
          <CSNoDataFound siteSoftDeleted={siteSoftDeleted} />
        ) : (
          <>
            {/* New Simplified Report UI */}
            <ContentStrategyReport
              data={data}
              siteAuditDetails={siteAuditDetails}
              userAgentDetails={userAgentDetails}
              onIgnoreOpportunity={handleIgnoreOpportunity}
              highlightOpportunity={highlightOpportunity}
            />
          </>
        )}
      </div>
    </div>
  );
}

// Old UI
// function ContentStrategyContent() {
//   const navigate = useNavigate();
//   const siteAuditDetails = useSelector(
//     (state: RootState) => state.siteAudit.siteAuditDetails
//   );
//   const siteId = siteAuditDetails?._id;
//   const user_id = siteAuditDetails?.user_id;
//   const siteSoftDeleted = useSelector(
//     (state: RootState) => state?.siteAudit?.isSiteSoftDeleted
//   );

//   const dispatch = useDispatch();

//   useOrbitAISSE(siteId);

//   const {
//     data,
//     isLoading,
//     isPending,
//     isFetching,
//     refetch: refetchStrategy
//   } = useQuery({
//     ...getContentStrategyQuery(user_id, siteId, ALL_STRATEGY_DATA_PARAMS),
//     placeholderData: keepPreviousData
//   });

//   const { data: agentsStatus } = useGetAgentsStatus(siteId);
//   const { addToPlanMutation, ignoreOpportunityMutation } =
//     useContentStrategyActions();

//   const [highlightOpportunity, setHighlightOpportunity] = useState<{
//     id: string;
//     ts: number;
//   } | null>(null);

//   const isContentStrategyProcessing =
//     agentsStatus?.agents?.['content-strategy']?.status === 'processing';

//   useEffect(() => {
//     if (!isContentStrategyProcessing && agentsStatus) {
//       refetchStrategy();
//     }
//   }, [isContentStrategyProcessing, agentsStatus, refetchStrategy]);

//   const hasNoData = useMemo(() => {
//     if (isContentStrategyProcessing) return false;
//     return (
//       !data ||
//       (!data.content_opportunities?.items?.length &&
//         !data.content_clusters?.items?.length &&
//         !data.existing_content_improvements?.items?.length)
//     );
//   }, [data, isContentStrategyProcessing]);

//   const handleSelectOpportunityFromInsight = (opportunityId: string) => {
//     setHighlightOpportunity({ id: opportunityId, ts: Date.now() });
//   };

//   // Todo: Content Plan - Handler to add opportunity to Content Plan
//   /*
//   const handleAddToPlan = (opportunityId: string) => {
//     addToPlanMutation.mutate(
//       { opportunityId },
//       {
//         onSuccess: () => {
//           dispatch(
//             setOrbitAISnackBar({
//               status: 'success',
//               message: 'Added to Content Plan'
//             })
//           );
//         }
//       }
//     );
//   };
//   */

//   const handleIgnoreOpportunity = (
//     opportunityId: string,
//     customEndpoint?: string
//   ) => {
//     if (highlightOpportunity?.id === opportunityId) {
//       setHighlightOpportunity(null);
//     }
//     ignoreOpportunityMutation.mutate({ opportunityId, customEndpoint });
//   };

//   if (isLoading || isPending) {
//     return (
//       <div
//         className="-mt-8 pt-8"
//         style={{
//           minHeight: 'calc(100vh - 51px)',
//           backgroundColor: 'var(--color-bg-cream)'
//         }}
//       >
//         <StripeLoader />
//       </div>
//     );
//   }

//   return (
//     <div
//       className="-mt-8 pt-4 pb-6"
//       style={{
//         minHeight: 'calc(100vh - 51px)',
//         backgroundColor: 'var(--color-bg-cream)'
//       }}
//     >
//       {/** Sticky Header with Breadcrumbs and Section Navigation */}
//       <div className="sticky top-[63px] z-10 bg-[var(--color-bg-cream)] -mx-5 px-5 pt-3 pb-3 mb-4 border-b border-[#EAECF0] transition-all">
//         <div className="flex justify-between items-center mb-2.5">
//           <ContentStrategyBreadcrumbs />
//           <div className="flex items-center gap-4">
//             <AgentLastRun
//               timestamp={!hasNoData && data?.last_updated ? data.last_updated : null}
//               showBorder
//             />
//             <ContentStrategyHeader />
//           </div>
//         </div>
//         {!isContentStrategyProcessing && !hasNoData && (
//           <ContentStrategyNav
//             hasSummary={Boolean(data?.ai_summary)}
//             hasInsights={Boolean(data?.ai_insights)}
//             hasOpportunities={Boolean(data?.content_opportunities)}
//             hasClusters={Boolean(data?.content_clusters)}
//             hasImprovements={Boolean(data?.existing_content_improvements)}
//           />
//         )}
//       </div>

//       <div className="mt-2">
//         {isContentStrategyProcessing ? (
//           <SEOCard>
//             <div className="px-6">
//               <p className="text-primary font-semibold text-3xl">
//                 Content Strategy
//               </p>
//               <div className="flex flex-col items-center justify-center gap-6 p-6 pb-6 w-full mt-2.5 mb-2.5">
//                 <div className="flex flex-col items-center justify-center gap-3 text-center min-h-[16rem]">
//                   <CircularProgress size={64} />
//                   <p className="font-semibold text-lg text-[#1D2939] mt-2">
//                     Orbit AI Content Strategy is running...
//                   </p>
//                   <p className="font-normal text-sm text-[#667085] max-w-md">
//                     This may take a few moments while we generate your
//                     customized content strategy and topic recommendations.
//                   </p>
//                 </div>
//               </div>
//             </div>
//           </SEOCard>
//         ) : hasNoData ? (
//           <CSNoDataFound siteSoftDeleted={siteSoftDeleted} />
//         ) : (
//           <>
//             {/** 1. AI Summary */}
//             {data?.ai_summary && (
//               <div
//                 id="section-cs-summary"
//                 className="scroll-mt-[175px]"
//                 style={{ scrollMarginTop: '175px' }}
//               >
//                 <AISummary summary={data.ai_summary} />
//               </div>
//             )}

//             {/** 2. AI Insight Floating Panel */}
//             {data?.ai_insights && (
//               <div
//                 id="section-cs-insights"
//                 className="scroll-mt-[165px]"
//                 style={{ scrollMarginTop: '165px' }}
//               >
//                 <AIInsightsPanel
//                   insights={data.ai_insights}
//                   opportunities={data?.content_opportunities?.items}
//                   onSelectOpportunity={handleSelectOpportunityFromInsight}
//                 />
//               </div>
//             )}

//             {/** 3. Content Opportunities */}
//             <div
//               id="section-cs-opportunities"
//               className="scroll-mt-[165px]"
//               style={{ scrollMarginTop: '165px' }}
//             >
//               <ContentOpportunities
//                 data={data?.content_opportunities}
//                 // Todo: Content Plan - Pass addToPlan handlers when Content Plan is enabled
//                 // onAddToPlan={handleAddToPlan}
//                 // isAddingToPlan={addToPlanMutation.isPending}
//                 onIgnore={handleIgnoreOpportunity}
//                 highlightOpportunity={highlightOpportunity}
//               />
//             </div>

//             {/** 4. Content Clusters */}
//             <div
//               id="section-cs-clusters"
//               className="scroll-mt-[165px]"
//               style={{ scrollMarginTop: '165px' }}
//             >
//               <ContentClusters data={data?.content_clusters} />
//             </div>

//             {/** 5. Existing Content Improvements */}
//             <div
//               id="section-cs-improvements"
//               className="scroll-mt-[165px]"
//               style={{ scrollMarginTop: '165px' }}
//             >
//               <ContentImprovement data={data?.existing_content_improvements} />
//             </div>
//           </>
//         )}
//       </div>
//     </div>
//   );
// }

function ContentStrategyPage() {
  return (
    <>
      <Helmet>
        <title>SEOSpace | Content Strategy</title>
      </Helmet>
      <OrbitAIGuard fallback={<ContentStrategyFallback />}>
        <OrbitAgentSiteGuard
          title={keywordResearchSubmenuTitles.contentStrategy}
          headerComponent={<ContentStrategyHeader />}
        >
          {/* <ContentStrategyContent /> */}
          <ContentStrategyContentNew />
        </OrbitAgentSiteGuard>
      </OrbitAIGuard>
    </>
  );
}

export default ContentStrategyPage;
